// 9fbef606107a605d69c0edbcd8029e5d
import React, { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import constant from '@tcp/core/src/components/common/molecules/ModuleQ/ModuleQ.constant';
import { getDimension2Value } from '@tcp/core/src/utils/utils.web';
import { isTier1Device } from '@tcp/web/src/utils/device-tiering';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import {
  getLabelFromCopyOrFromTranslation,
  getDynamicLabelValues,
  renderLabel,
} from '@tcp/core/src/utils/labels/labelUtilty';
import { PDP_SUB_LABELS, pdpSubLabels } from '@tcp/core/src/utils/labels/browse/PDP.labels';
import { OUTFIT_LABELS, outfitLabels } from '@tcp/core/src/utils/labels/browse/Outfit.labels';
import {
  GlobalAccessibilityLabels,
  GLOBAL_ACCESSIBILITY_LABELS,
} from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import {
  GlobalActionsLabels,
  GLOBAL_ACTIONS_LABELS,
} from '@tcp/core/src/utils/labels/globalActions.labels';
import { getOriginalPrice } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/views/ProductGridItem.util';
import { checkInWishlistUtil } from '../../ProductListing/molecules/ProductList/utils/productsCommonUtils';
import withStyles from '../../../../common/hoc/withStyles';
import OutfitDetailsStyle, { recommendationStyles } from '../OutfitDetails.style';
import OutfitProduct from '../molecules/OutfitProduct/OutfitProduct';
import { routerPush, getIcidValue, getBrandInCaps, fireEnhancedEcomm } from '../../../../../utils';
import PromoPDPBanners from '../../../../common/organisms/PromoPDPBanners';
import names from '../../../../../constants/eventsName.constants';
import pushEventToAdobeAnalytics from '../../../../../adobeAnalytics/adobe.utils';
import ToastModal from '../../../account/common/molecule/ToastModal/views/ToastModal';

const Recommendations = dynamic(() =>
  import(
    // eslint-disable-next-line extra-rules/no-commented-out-code
    /* webpackChunkName: "recommendations" */ '@tcp/core/src/components/common/molecules/Recommendations'
  )
);

const routesBack = (e) => {
  e.preventDefault();
  if (window.history.length > 2) window.history.back();
  else {
    routerPush('/', '/home');
  }
};

const PAGE_NAME = Constants.RECOMMENDATIONS_PAGES_MAPPING.OUTFIT;

const getRecsProductId = (tile, viaModule, viaPage, icidParam) => {
  if (
    ((icidParam === '' || icidParam == null) && viaModule === constant.RECOMMENDATION) ||
    viaPage === 'CLP'
  ) {
    return tile.generalProductId;
  }
  return '';
};

const ON_SALE_PRICING_STATE = 'on sale';
const FULL_PRICE_PRICING_STATE = 'full price';

const formatProductsData = (outfitProducts, viaPage, viaModule = '', icidParam = '') => {
  const completeTheLook = getLabelFromCopyOrFromTranslation(
    `${PDP_SUB_LABELS}.lbl_complete_the_look`,
    pdpSubLabels
  );
  return outfitProducts.map((tile) => {
    const colorName = tile.colorFitsSizesMap.map((productTile) => {
      return productTile.color.name || '';
    });
    const productId = tile.generalProductId && tile.generalProductId.split('_')[0];
    return {
      colorId: tile.generalProductId,
      color: (colorName && colorName[0]) || '',
      id: productId,
      outfitId: tile.categoryId,
      name: tile.name,
      price: tile.offerPrice,
      offerPrice: tile.offerPrice,
      listPrice: tile.listPrice,
      pricingState:
        tile.offerPrice < tile.listPrice ? ON_SALE_PRICING_STATE : FULL_PRICE_PRICING_STATE,
      rating: tile.ratings,
      reviews: tile.reviewsCount,
      recsProductId: getRecsProductId(tile, viaModule, viaPage, icidParam),
      features: 'alt images:full size image:size chart',
      dimension92:
        viaModule && (viaModule === constant.RECOMMENDATION ? completeTheLook : viaModule),
      recsPageType: viaPage,
    };
  });
};

const getRecs = (args) => {
  const completeTheLook = getLabelFromCopyOrFromTranslation(
    `${PDP_SUB_LABELS}.lbl_complete_the_look`,
    pdpSubLabels
  );
  const { tile, viaModule, viaPage, icidParam = '' } = args;
  const cd92 = viaModule && (viaModule === constant.RECOMMENDATION ? completeTheLook : viaModule);
  return {
    dimension20: getRecsProductId(tile, viaModule, viaPage, icidParam),
    dimension92: cd92,
    dimension21: viaPage,
    metric50: '1',
    dimension2: getDimension2Value(cd92) || 'outfit',
  };
};

const formatProductsDataEE = (
  outfitProducts,
  viaPage,
  viaModule = '',
  icidParam = '',
  isHideOriginalPriceAndMarchantTagEnabled = ''
) => {
  const brand = getBrandInCaps();
  return outfitProducts.map((tile) => {
    const colorName = tile.colorFitsSizesMap.map((productTile) => {
      return productTile.color.name || '';
    });
    const recsDimensions = getRecs({ tile, viaModule, viaPage, icidParam });
    let category = '';
    if (recsDimensions && recsDimensions.dimension92) {
      category = `${recsDimensions.dimension92}-${recsDimensions.dimension2 || ''}`;
    }

    const originalPrice = getOriginalPrice({
      hardMarkDown: tile.hardMarkDown,
      isHideOriginalPriceAndMarchantTagEnabled,
      currency: '',
      listPrice: tile.listPrice,
    });

    return {
      variant: (colorName && colorName[0]) || '',
      id: tile.generalProductId,
      price: tile.offerPrice,
      metric75: '1',
      dimension35: brand,
      category,
      dimension60: tile.categoryId,
      dimension63: `${tile.offerPrice} ${originalPrice}`,
      dimension67:
        tile.offerPrice < tile.listPrice ? ON_SALE_PRICING_STATE : FULL_PRICE_PRICING_STATE,
      dimension71: tile.ratings,
      dimension72: 'alt images:full size image:size chart',
      dimension77: (colorName && colorName[0]) || '',
      dimension89: tile.generalProductId,
      ...recsDimensions,
    };
  });
};

const OutfitDetailsView = ({
  className,
  outfitImageUrl,
  unavailableCount,
  outfitProducts,
  handleAddToBag,
  addToBagEcom,
  currentState,
  addToBagError,
  addToBagErrorId,
  isLoggedIn,
  addToFavorites,
  currencyAttributes,
  currencySymbol,
  outfitId,
  AddToFavoriteErrorMsg,
  removeAddToFavoritesErrorMsg,
  asPathVal,
  topPromos,
  trackPageLoad,
  isKeepAliveEnabled,
  isOnModelAbTestOutfitPdp,
  isInternationalShipping,
  viaModule,
  storeId,
  viaPage,
  isEnabledOutfitAddedCTA,
  showAddedToBagCta,
  priceCurrency,
  isDynamicBadgeEnabled,
  multiPackThreshold,
  onSetLastDeletedItemIdAction,
  wishList,
  isOutfitPage,
  trackWishListClick,
  isPlcc,
  isHideOriginalPriceAndMarchantTagEnabled,
  isBundleOutfitInfluencerHandleEnabled,
  toastModalState,
  showToastModalAction,
}) => {
  const dynamicBrowseLabels = getDynamicLabelValues('dynamic.browse_d');

  const youMayAlsoLike = renderLabel(
    dynamicBrowseLabels.lbl_you_may_also_like_outfit,
    'lbl_you_may_also_like_outfit'
  );

  const recentlyViewed = renderLabel(
    dynamicBrowseLabels.lbl_recently_viewed_outfit,
    'lbl_recently_viewed_outfit'
  );

  const outfitLabelsObj = getLabelFromCopyOrFromTranslation(OUTFIT_LABELS, outfitLabels);

  const outfitUnavailableLabel = renderLabel(
    outfitLabelsObj.lbl_outfit_unavailable,
    'lbl_outfit_unavailable'
  );

  const backLabel = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_ACTIONS_LABELS}.lbl_back`,
    GlobalActionsLabels
  );

  const outfitImageLbl = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_ACCESSIBILITY_LABELS}.lbl_outfit_image`,
    GlobalAccessibilityLabels
  );
  const isCompleteTheLookProduct = useRef(false);
  const recommendationAttributes = {
    variations: 'moduleO',
    page: PAGE_NAME,
    partNumber: outfitId,
    showLoyaltyPromotionMessage: false,
    headerAlignment: 'left',
    priceOnly: true,
    recommendationCarouselRedesign: true,
  };
  const willAlsoViewedCarouselDisplay = isTier1Device();

  useEffect(() => {
    const icidParam = getIcidValue(asPathVal);
    const productsFormatted = formatProductsData(outfitProducts, viaPage, viaModule, icidParam);
    const productsFormattedEE = formatProductsDataEE(
      outfitProducts,
      viaPage,
      viaModule,
      icidParam,
      isHideOriginalPriceAndMarchantTagEnabled
    );
    const customEvents = ['prodView', 'event62', 'event75', 'event80'];
    let additionalData = {
      internalCampaignId: '',
      pageNavigationText: '',
    };

    if (icidParam) {
      additionalData = {
        internalCampaignId: icidParam,
        pageNavigationText: '',
      };
    } else if (viaModule === constant.RECOMMENDATION) {
      isCompleteTheLookProduct.current = true;
      customEvents.push(names.eVarPropEvent.event50);
      const recsTypeForAA = 'Complete The Look';
      pushEventToAdobeAnalytics('cross_sell_clicks', recsTypeForAA);
      additionalData = {
        internalCampaignId: '',
        pageNavigationText: '',
      };
    }

    if (outfitProducts.length) {
      trackPageLoad({
        products: productsFormatted,
        pageType: 'outfit',
        pageName: productsFormatted.length
          ? `outfit:${productsFormatted[0].id}:${productsFormatted[0].name}`
          : 'outfit',
        pageSection: 'outfit',
        pageSubSection: 'outfit',
        customEvents,
        storeId,
        productId: productsFormatted[0].id,
        ...additionalData,
      });
      fireEnhancedEcomm({
        eventName: 'productDetail',
        productsObj: productsFormattedEE,
        eventType: 'detail',
        pageName: 'Outfit PDP',
        currCode: priceCurrency,
        eventLabel: 'Product detail',
      });
    }
  }, [outfitProducts.length]);
  const toastModal = JSON.parse(JSON.stringify(toastModalState));
  return (
    <div className={className}>
      <Row>
        <Col
          colSize={{
            small: 6,
            medium: 8,
            large: 12,
          }}
          ignoreGutter={{ small: true }}
          className="outfit-back-button"
        >
          <h1 className="outfit-title">Outfit Category</h1>
          <Anchor
            fontSizeVariation="xlarge"
            anchorVariation="secondary"
            handleLinkClick={routesBack}
            noLink
            className={`${className}__backlink`}
            title={backLabel}
          >
            <span className="left-arrow" />
            {backLabel}
          </Anchor>
        </Col>
      </Row>
      {topPromos && topPromos.length > 0 && (
        <Row>
          <Col className="promo-area-top" colSize={{ small: 6, medium: 8, large: 12 }}>
            <PromoPDPBanners promos={topPromos} asPath={asPathVal} />
          </Col>
        </Row>
      )}
      <Row>
        <Col
          colSize={{ small: 6, medium: 3, large: 6 }}
          ignoreGutter={{ small: true }}
          className="outfit-image"
        >
          <Image className="promo-area-0" src={outfitImageUrl} alt={outfitImageLbl} />
        </Col>
        <hr className="outfit-line-break" />
        <Col
          colSize={{ small: 6, medium: 5, large: 6 }}
          ignoreGutter={{ small: true, medium: true, large: true }}
        >
          <ul className="outfiting-list-container">
            {outfitProducts &&
              outfitProducts.map((product, index) => (
                <li data-prodId={product.generalProductId} key={product.generalProductId}>
                  <OutfitProduct
                    isPlcc={isPlcc}
                    outfitProduct={product}
                    productIndexText={`Product ${index + 1} of ${outfitProducts.length}`}
                    handleAddToBag={() => {
                      handleAddToBag(addToBagEcom, product, product.generalProductId, currentState);
                    }}
                    className="outfiting-list-details"
                    addToBagError={addToBagErrorId === product.generalProductId && addToBagError}
                    isLoggedIn={isLoggedIn}
                    addToFavorites={addToFavorites}
                    currencySymbol={currencySymbol}
                    currencyAttributes={currencyAttributes}
                    AddToFavoriteErrorMsg={AddToFavoriteErrorMsg}
                    removeAddToFavoritesErrorMsg={removeAddToFavoritesErrorMsg}
                    pageName="OUTFIT"
                    isKeepAliveEnabled={isKeepAliveEnabled}
                    isOnModelAbTestOutfitPdp={isOnModelAbTestOutfitPdp}
                    isInternationalShipping={isInternationalShipping}
                    showAddedToBagCta={
                      isEnabledOutfitAddedCTA && showAddedToBagCta === product.generalProductId
                    }
                    isDynamicBadgeEnabled={isDynamicBadgeEnabled}
                    multiPackThreshold={multiPackThreshold}
                    getAllNewMultiPack={product.getNewMultiPackAllColor}
                    isOutfitPage={isOutfitPage}
                    isInWishList={checkInWishlistUtil(wishList, product.productId)}
                    onSetLastDeletedItemIdAction={onSetLastDeletedItemIdAction}
                    wishList={wishList}
                    trackWishListClick={trackWishListClick}
                    isHideOriginalPriceAndMarchantTagEnabled={
                      isHideOriginalPriceAndMarchantTagEnabled
                    }
                    influencerTags={product?.colorFitsSizesMap?.[0].influencerTags}
                    isBundleOutfitInfluencerHandleEnabled={isBundleOutfitInfluencerHandleEnabled}
                    outfitItemIndex={index}
                  />
                </li>
              ))}

            {!!unavailableCount && (
              <BodyCopy
                textAlign="left"
                fontFamily="secondary"
                fontSize="fs16"
                className="elem-mt-MED elem-mb-MED"
              >
                {`${unavailableCount} ${outfitUnavailableLabel}`}
              </BodyCopy>
            )}
          </ul>
        </Col>
        <Col
          colSize={{ small: 6, medium: 8, large: 12 }}
          ignoreGutter={{ small: true, medium: true, large: true }}
        >
          <div className="placeholder promo-area-1">{outfitLabelsObj.lbl_complete_the_look}</div>
        </Col>
        <Col colSize={{ small: 6, medium: 8, large: 12 }}>
          <Recommendations
            inheritedStyles={recommendationStyles}
            headerLabel={youMayAlsoLike}
            page={PAGE_NAME}
            {...recommendationAttributes}
            sequence="1"
            isOutFitPage
          />
        </Col>
        {willAlsoViewedCarouselDisplay && (
          <Col colSize={{ small: 6, medium: 8, large: 12 }}>
            <Recommendations
              inheritedStyles={recommendationStyles}
              headerLabel={recentlyViewed}
              portalValue={Constants.RECOMMENDATIONS_MBOXNAMES.RECENTLY_VIEWED}
              page={PAGE_NAME}
              {...recommendationAttributes}
              sequence="2"
              isOutFitPage
            />
          </Col>
        )}
      </Row>
      {toastModal?.showToast && (
        <ToastModal
          className={className}
          duration={toastModal?.duration}
          showToastModalAction={showToastModalAction}
        />
      )}
    </div>
  );
};

OutfitDetailsView.propTypes = {
  className: PropTypes.string,
  outfitImageUrl: PropTypes.string,
  unavailableCount: PropTypes.number,
  outfitProducts: PropTypes.shape({}),
  addToBagEcom: PropTypes.func.isRequired,
  handleAddToBag: PropTypes.func.isRequired,
  currentState: PropTypes.shape({}).isRequired,
  addToBagError: PropTypes.string,
  addToBagErrorId: PropTypes.string,
  addToFavorites: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool,
  currencyAttributes: PropTypes.shape({}).isRequired,
  currencySymbol: PropTypes.string,
  outfitId: PropTypes.string,
  AddToFavoriteErrorMsg: PropTypes.string,
  removeAddToFavoritesErrorMsg: PropTypes.func,
  isKeepAliveEnabled: PropTypes.bool,
  asPathVal: PropTypes.string,
  topPromos: PropTypes.string,
  trackPageLoad: PropTypes.func,
  isOnModelAbTestOutfitPdp: PropTypes.bool,
  isInternationalShipping: PropTypes.bool.isRequired,
  viaModule: PropTypes.string,
  storeId: PropTypes.string,
  viaPage: PropTypes.string,
  isEnabledOutfitAddedCTA: PropTypes.bool,
  showAddedToBagCta: PropTypes.string,
  priceCurrency: PropTypes.string,
  isDynamicBadgeEnabled: PropTypes.shape({}),
  multiPackThreshold: PropTypes.string,
  isInWishList: PropTypes.bool.isRequired,
  onSetLastDeletedItemIdAction: PropTypes.func,
  wishList: PropTypes.shape({}),
  isOutfitPage: PropTypes.bool,
  isPlcc: PropTypes.bool.isRequired,
  checkInWishlist: PropTypes.func,
  trackWishListClick: PropTypes.func,
  toastModalState: PropTypes.shape({
    duration: PropTypes.number,
    showToast: PropTypes.bool,
  }),
  showToastModalAction: PropTypes.func,
};

OutfitDetailsView.defaultProps = {
  className: '',
  outfitImageUrl: '',
  outfitProducts: null,
  unavailableCount: 0,
  addToBagError: '',
  addToBagErrorId: '',
  isLoggedIn: false,
  currencySymbol: 'USD',
  outfitId: '',
  AddToFavoriteErrorMsg: '',
  removeAddToFavoritesErrorMsg: () => {},
  trackPageLoad: () => {},
  isKeepAliveEnabled: false,
  asPathVal: '',
  topPromos: '',
  isOnModelAbTestOutfitPdp: false,
  viaModule: '',
  storeId: '',
  viaPage: '',
  isEnabledOutfitAddedCTA: false,
  showAddedToBagCta: '',
  priceCurrency: 'USD',
  isDynamicBadgeEnabled: false,
  multiPackThreshold: '',
  onSetLastDeletedItemIdAction: () => {},
  wishList: {},
  isOutfitPage: true,
  checkInWishlist: () => {},
  trackWishListClick: () => {},
  toastModalState: {
    duration: 0,
    showToast: false,
  },
  showToastModalAction: () => {},
};

export default withStyles(OutfitDetailsView, OutfitDetailsStyle);
